import { ChildModel, ModelBase } from 'store/model-base';
import { DataPlanType, GeminiCBMNoLevelSubgroup, OrderItemCategory, OrderItemType, PaymentCaptureStatus, PlanEightSubgroup, ReturnStatus, SimType, UnlOrdersPlanSubgroupType } from 'my-account/constants';
import { OrderItemCharge } from './charge';
import { OrderReturnInfo } from './return';
import { ReturnFee } from './return-fee';
import { OrderItemPromotion } from './promotion';
import { OS, ProgramType } from 'core/constants';

export class OrderItem extends ModelBase {
    public static storeName: string = 'orderItem';
    public static idAttribute: string = 'orderItemId';

    public orderItemId: string;
    public type: OrderItemType;
    public planDetails: OrderItemPlanDetails;
    public deviceIccid: string;
    public deviceImei: string;
    public displayName: string;
    public lineNumber: string; // can be undefined for new number
    public productId: string;
    public return: OrderReturnInfo;
    public returnFees: ReturnFee[];
    public ratePlans: RatePlan[];
    public currentPlan: CurrentRatePlan;
    public byodCarrier: string;
    public upgradeFee: number;
    public itemTotal: number;
    public subTotal: number;
    public fullPrice: number;
    public paymentCaptureStatus: PaymentCaptureStatus;
    public trackingNumber: string;
    public productDetails: LineDeviceDetail;
    public byod: boolean;
    public simType: string;
    public charges: OrderItemCharge[];
    public promotions: OrderItemPromotion[];
    public integratedTradeIn: OrderItemIntegratedTradeInInfo;
    public category: OrderItemCategory;
    public displayItemTotal: number;
    public tradeIn: OrderItemTradeInInfo[];

    public isTablet: boolean;
    public isRegularOrder: boolean;
    public isPreOrderItem: boolean;
    public isBackOrderItem: boolean;
    public isByodDeviceLocked: boolean;
    public isNdelItem: boolean;
    public isNdelBackOrderItem: boolean;
    public isNdelPreOrderItem: boolean;
    public isUpgradeItem: boolean;
    public isUpgradePreOrderItem: boolean;
    public isUpgradeBackOrderItem: boolean;
    public isSimLedByod: boolean;

    public isAccessory: boolean;
    public isPhone: boolean;
    public isWatch: boolean;
    public isStandaloneWatch: boolean;
    public isReturnItem: boolean;
    public isCeaseItem: boolean;
    public isExchangeItem: boolean;
    public isWarrantyExchangeItem: boolean;
    public isRefundItem: boolean;
    public isChargedItem: boolean;
    public isByTheGigShared: boolean;
    public isByTheGig: boolean;
    public isUnlimited: boolean;
    public isSmartwatch: boolean;
    public hasExtendedPromo: boolean;
    public hasPrepaidPromo: boolean;
    public hasSinglePromo: boolean;
    public hasAnyExtendedPromo: boolean;
    public hasTradeInGiftCardPromo: boolean;
    public hasXMCPromotion: boolean;
    public productMakeAndModal: string;
    public productColorAndSize: string;
    public isIosDevice: boolean;
    public isEsim: boolean;
    public isTIP: boolean;
    public tradeInPromoDevice: OrderItemPromotion;
    public tradeInPlusUpPromo: OrderItemPromotion;
    public isUnlIntro: boolean;
    public isUnlPlus: boolean;
    public isUnlPremium: boolean;
    public isMobileSelect: boolean;
    public isMobilePlus: boolean;
    public isProGroup: boolean;
    public isStandardGroup: boolean;
    public isPsimByodOrder: boolean;
    public hasServicePromoLine: boolean;
    public hasServicePromoAccount: boolean;
    public tradeInDetails: OrderItemTradeInInfo;
    public earlyUpgradeDetails: OrderItemTradeInInfo;
    public isTabletGroup: boolean;
    public isChangePlan: boolean;
    public isChangeSegment: boolean;

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'charges',
            model: OrderItemCharge
        }, {
            attrName: 'returnFees',
            model: ReturnFee
        }, {
            attrName: 'promotions',
            model: OrderItemPromotion
        }];
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'return',
            model: OrderReturnInfo
        }];
    }

    public static create<T extends ModelBase>(initData: ApiResponse): T {
        const toReturn: OrderItem = super.create<OrderItem>(initData);
        toReturn.isTablet = toReturn.category === OrderItemCategory.TABLET;
        toReturn.isWatch = toReturn.category === OrderItemCategory.SMART_WATCH;
        toReturn.isStandaloneWatch = toReturn.category === OrderItemCategory.SMART_WATCH && Boolean(toReturn.lineNumber);
        toReturn.isRegularOrder = toReturn.type === OrderItemType.REGULAR;
        toReturn.isPreOrderItem = toReturn.type === OrderItemType.PREORDER;
        toReturn.isBackOrderItem = toReturn.type === OrderItemType.BACKORDER;
        toReturn.isNdelItem = toReturn.type === OrderItemType.NDEL;
        toReturn.isNdelBackOrderItem = toReturn.type === OrderItemType.NDEL_BACKORDER;
        toReturn.isNdelPreOrderItem = toReturn.type === OrderItemType.NDEL_PREORDER;
        toReturn.isUpgradeItem = toReturn.type === OrderItemType.UPGRADE;
        toReturn.isUpgradePreOrderItem = toReturn.type === OrderItemType.UPGRADE_PREORDER;
        toReturn.isUpgradeBackOrderItem = toReturn.type === OrderItemType.UPGRADE_BACKORDER;
        toReturn.isSimLedByod = toReturn.type === OrderItemType.SIMLED_BYOD;
        toReturn.isAccessory = Boolean(toReturn.category && toReturn.category.toUpperCase() === OrderItemCategory.ACCESSORY);
        toReturn.isPhone = Boolean(toReturn.category && toReturn.category.toUpperCase() === OrderItemCategory.PHONE);
        toReturn.isIosDevice = Boolean(toReturn.productDetails && toReturn.productDetails.os && toReturn.productDetails.os.toUpperCase() === OS.IOS);
        toReturn.isTIP = Boolean(toReturn.integratedTradeIn && toReturn.integratedTradeIn.program === ProgramType.TIP);
        toReturn.isReturnItem = toReturn.type === OrderItemType.RETURN;
        toReturn.isCeaseItem = toReturn.type === OrderItemType.CEASE;
        toReturn.isExchangeItem = toReturn.type === OrderItemType.EXCHANGE;
        toReturn.isWarrantyExchangeItem = toReturn.type === OrderItemType.WARRANTY_EXCHANGE;
        toReturn.isChangePlan = toReturn.type === OrderItemType.CHANGE_PLAN;
        toReturn.isChangeSegment = toReturn.type === OrderItemType.CHANGE_SEGMENT;
        toReturn.isEsim = toReturn.simType === SimType.ESIM;
        toReturn.isPsimByodOrder = toReturn.simType === SimType.PSIM && toReturn.byod;

        // Build planDetails from currentPlan for change plan/segment orders when planDetails is empty
        if ((toReturn.isChangePlan || toReturn.isChangeSegment) && !toReturn.planDetails && toReturn.currentPlan) {
            toReturn.planDetails = {
                planGroup: toReturn.currentPlan.planGroup,
                planSubgroup: toReturn.currentPlan.planSubgroup,
                planId: toReturn.currentPlan.planCode,
                dataPassSize: 0,
                planPrice: 0
            };
        }

        if (toReturn.tradeIn) {
            toReturn.tradeInDetails = toReturn.tradeIn.find((item: OrderItemTradeInInfo) => item.program === ProgramType.TIP);
            toReturn.earlyUpgradeDetails = toReturn.tradeIn.find((item: OrderItemTradeInInfo) => item.program === ProgramType.EARLY_UPGRADE);
        }

        if (toReturn.promotions) {
            toReturn.hasSinglePromo = toReturn.promotions.length === 1;
            toReturn.hasExtendedPromo = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isExtendedPromo);
            toReturn.hasPrepaidPromo = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isPrepaid);
            toReturn.hasAnyExtendedPromo = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isExtendedPromo || promotion.isExtendedPromoDollar || promotion.isExtendedPromoPercentage);
            toReturn.tradeInPromoDevice = toReturn.promotions.find((promotion: OrderItemPromotion) =>  promotion.isTradeInPromo);
            toReturn.hasServicePromoLine = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isServiceLinePromo);
            toReturn.hasServicePromoAccount = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isServiceAccountPromo);
            toReturn.hasTradeInGiftCardPromo = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isTradeInGiftCard);
            toReturn.hasXMCPromotion = toReturn.promotions.some((promotion: OrderItemPromotion) => promotion.isXMCPromotion);
            toReturn.tradeInPlusUpPromo = toReturn.promotions.find((promotion: OrderItemPromotion) => promotion.isTradeInPromoPlusUp);
        }

        if (toReturn.return) {
            toReturn.isRefundItem = toReturn.return.status === ReturnStatus.REFUNDED;
            toReturn.isChargedItem = toReturn.return.status === ReturnStatus.CHARGED;
        }
        if (toReturn.planDetails) {
            toReturn.isByTheGigShared = toReturn.planDetails.planGroup === DataPlanType.BY_THE_GIG_SHARED;
            toReturn.isByTheGig = toReturn.planDetails.planGroup === DataPlanType.BY_THE_GIG;
            toReturn.isUnlimited = toReturn.planDetails.planGroup === DataPlanType.UNLIMITED;
            toReturn.isSmartwatch = toReturn.planDetails.planGroup === DataPlanType.SMART_WATCH;
            toReturn.isTabletGroup = toReturn.planDetails.planGroup === DataPlanType.TABLET;
            toReturn.isUnlIntro = toReturn.planDetails.planSubgroup === UnlOrdersPlanSubgroupType.BASIC;
            toReturn.isUnlPlus = toReturn.planDetails.planSubgroup === UnlOrdersPlanSubgroupType.PLUS;
            toReturn.isUnlPremium = toReturn.planDetails.planSubgroup === UnlOrdersPlanSubgroupType.PREMIUM;
            toReturn.isMobileSelect = toReturn.planDetails.planSubgroup === PlanEightSubgroup.SELECT;
            toReturn.isMobilePlus = toReturn.planDetails.planSubgroup === PlanEightSubgroup.PLUS;
            toReturn.isProGroup = toReturn.planDetails.planSubgroup === GeminiCBMNoLevelSubgroup.PRO;
            toReturn.isStandardGroup = toReturn.planDetails.planSubgroup === GeminiCBMNoLevelSubgroup.STANDARD;
        }

        toReturn.productMakeAndModal = toReturn.productDetails && `${toReturn.productDetails.brand || ''} ${toReturn.productDetails.model || ''}`;
        toReturn.productColorAndSize = toReturn.productDetails && `${toReturn.productDetails.colorName || ''}, ${toReturn.productDetails.capacity || ''}`;
        toReturn.displayItemTotal = toReturn.isReturnItem ? toReturn.subTotal : toReturn.itemTotal;

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    public get productName(): string {
        if (this.productDetails) {
            const product: LineDeviceDetail = this.productDetails;

            return `${product.brand || ''} ${product.model || ''}, ${product.colorName || ''}, ${product.capacity || ''}`;
        }
    }

    public get isPlanUnlimited(): boolean {
        return this.charges.some((charge: OrderItemCharge) => charge.isUnlimited);
    }

    public get isLineServicePrimary(): boolean {
        return this.charges.some((charge: OrderItemCharge) => charge.isLineServicePrimary);
    }

    public get isActivationFee(): boolean {
        return this.charges.some((charge: OrderItemCharge) => charge.isActivationFee);
    }

    public get isActivationWaived(): boolean {
        return this.charges.some((charge: OrderItemCharge) => charge.isActivationWaived);
    }
}

